import React, { useMemo, useState } from 'react'
import { FileActions } from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './FileActionsShowcase.css'

const FileActionsShowcase: React.FC = () => {
  const [darkMode, setDarkMode] = useState(false)
  const [selectedCount, setSelectedCount] = useState(3)
  const [lastAction, setLastAction] = useState(
    'Select files, then use the toolbar actions.',
  )

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'file-actions-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="file-actions-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
      {
        title: 'Content',
        fields: [
          {
            id: 'file-actions-count',
            label: 'Selected count',
            control: (
              <input
                id="file-actions-count"
                className="ui-field-control"
                type="number"
                min={0}
                max={99}
                value={selectedCount}
                onChange={(event) =>
                  setSelectedCount(Number(event.target.value) || 0)
                }
              />
            ),
          },
        ],
      },
    ],
    [darkMode, selectedCount],
  )

  const liveHandlers = {
    onClear: () => {
      setSelectedCount(0)
      setLastAction('Cleared selection.')
    },
    onDownload: () => setLastAction(`Download ${selectedCount} item(s).`),
    onMove: () => setLastAction(`Move ${selectedCount} item(s).`),
    onCopy: () => setLastAction(`Make a copy of ${selectedCount} item(s).`),
    onDelete: () => setLastAction(`Delete ${selectedCount} item(s).`),
  }

  return (
    <>
      <UiPreviewCanvas
        title="File actions"
        description="640px selection bar — close, (x) Selected, then download, move, copy, and delete. Delete stays red."
        stageClassName="ui-preview__stage--wide file-actions-showcase__stage"
      >
        <div
          className={[
            'file-actions-showcase__sheet',
            darkMode ? 'file-actions-showcase__sheet--dark' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Matches the Files multi-select bar. Click actions to update the
              status below.
            </p>
            <div
              className={`file-actions-showcase__panel${
                darkMode ? ' file-actions-showcase__panel--dark' : ''
              }`}
            >
              <FileActions
                selectedCount={selectedCount}
                darkMode={darkMode}
                {...liveHandlers}
              />
            </div>
            <p className="file-actions-showcase__status">{lastAction}</p>
          </section>

          <section className="file-actions-showcase__matrix">
            <h4 className="file-actions-showcase__section-title">
              Light and dark
            </h4>
            <p className="ui-showcase-live__note">
              Light is white with a `blue-200` edge. Icons and label use
              `blue-600`; delete uses `red-500`. Dark sits on `blue-900` with a
              `blue-700` edge.
            </p>
            <div className="file-actions-showcase__panel">
              <FileActions
                selectedCount={selectedCount}
                onClear={() => undefined}
                onDownload={() => undefined}
                onMove={() => undefined}
                onCopy={() => undefined}
                onDelete={() => undefined}
              />
            </div>
            <div className="file-actions-showcase__panel file-actions-showcase__panel--dark">
              <FileActions
                selectedCount={selectedCount}
                darkMode
                onClear={() => undefined}
                onDownload={() => undefined}
                onMove={() => undefined}
                onCopy={() => undefined}
                onDelete={() => undefined}
              />
            </div>
          </section>
        </div>
      </UiPreviewCanvas>
      <UiPropertiesPanel
        componentName="File actions"
        sections={propertySections}
      />
    </>
  )
}

export default FileActionsShowcase
